import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../services/api';
import {
  ClockIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import FoundersBadge from './FoundersBadge';

/**
 * Fetch beta trial status for a company
 */
async function fetchBetaTrialStatus(companyId) {
  const response = await api.get(`/billing/beta-status/${companyId}`);
  return response.data;
}

/**
 * BetaTrialStatus Component
 * Displays the current beta trial status with countdown and upgrade prompts
 * @param {string} companyId - The company ID to fetch status for
 * @param {function} onUpgradeClick - Optional callback when upgrade button is clicked (if not provided, links to billing page)
 */
export default function BetaTrialStatus({ companyId, onUpgradeClick }) {
  const { data: status, isLoading, error } = useQuery({
    queryKey: ['betaTrialStatus', companyId],
    queryFn: () => fetchBetaTrialStatus(companyId),
    enabled: !!companyId,
    staleTime: 60000, // 1 minute
    refetchInterval: 60000 // Refresh every minute
  });

  if (isLoading || error || !status?.isBetaUser) {
    return null;
  }

  // Determine display based on status
  const isTrialActive = status.status === 'trial_active';
  const isGracePeriod = status.status === 'grace_period';
  const isUpgraded = status.status === 'upgraded';
  const isExpired = status.status === 'expired';
  
  // Calculate urgency colors
  const daysLeft = isTrialActive ? status.daysRemaining : status.graceDaysRemaining;
  const isUrgent = daysLeft <= 3;
  const isWarning = daysLeft <= 7 && daysLeft > 3;

  if (isUpgraded) {
    return <BetaUpgradedStatus status={status} />;
  }

  if (isExpired) {
    return <BetaExpiredStatus />;
  }

  return (
    <div className={`rounded-xl border p-4 ${
      isGracePeriod
        ? 'bg-red-500/5 border-red-500/30'
        : isUrgent
          ? 'bg-amber-500/5 border-amber-500/30'
          : isWarning
            ? 'bg-amber-500/5 border-amber-500/20'
            : 'bg-purple-500/5 border-purple-500/30'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${
            isGracePeriod
              ? 'bg-red-500/10 text-red-500'
              : isUrgent || isWarning
                ? 'bg-amber-500/10 text-amber-500'
                : 'bg-purple-500/10 text-purple-500'
          }`}>
            {isGracePeriod ? (
              <ExclamationTriangleIcon className="w-5 h-5" />
            ) : (
              <ClockIcon className="w-5 h-5" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold ${
                isGracePeriod ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-purple-400'
              }`}>
                {isGracePeriod ? 'Grace Period Active' : 'Beta Trial'}
              </h3>
              <FoundersBadge size="small" showTooltip={false} />
            </div>
            <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
              {isGracePeriod ? (
                <>The beta has ended. Choose a plan within <strong className="text-red-400">{daysLeft} days</strong> or be downgraded to Starter.</>
              ) : (
                <>The beta ends in <strong className={isUrgent ? 'text-amber-400' : 'text-purple-400'}>{daysLeft} days</strong> ({status.trialEndDate ? new Date(status.trialEndDate).toLocaleDateString() : 'TBD'}).</>
              )}
            </p>
            
            {/* Special pricing reminder */}
            <div className="mt-3 p-2 bg-surface-400/50 rounded-lg">
              <div className="text-xs text-surface-400 flex items-start gap-2">
                <SparklesIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-600">Founders Pricing - Upgrade before the beta ends!</p>
                  <ul className="mt-1 space-y-0.5 text-surface-500">
                    <li>• <strong className="text-blue-600">Basic:</strong> Free during beta, then <strong className="text-blue-600">50% off</strong> for your first year</li>
                    <li>• <strong className="text-blue-600">Pro/Business:</strong> $10/$25 first month, then <strong className="text-blue-600">50% off</strong> for 11 months</li>
                    <li className="text-blue-600 italic">• Discount is locked to the plan you choose!</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {onUpgradeClick ? (
          <button
            onClick={onUpgradeClick}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              isGracePeriod
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-purple-500 hover:bg-purple-600 text-white'
            }`}
          >
            <ArrowUpIcon className="w-4 h-4" />
            Upgrade
          </button>
        ) : (
          <Link
            to="/admin/billing"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              isGracePeriod
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-purple-500 hover:bg-purple-600 text-white'
            }`}
          >
            <ArrowUpIcon className="w-4 h-4" />
            Upgrade
          </Link>
        )}
      </div>
      
    </div>
  );
}

/**
 * Status display for upgraded beta users
 */
function BetaUpgradedStatus({ status }) {
  const discountRemaining = status.betaDiscountExpiresAt 
    ? Math.ceil((new Date(status.betaDiscountExpiresAt) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;
  
  if (discountRemaining <= 0) {
    return null;
  }
  
  return (
    <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/30 p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-500">
          <CheckCircleIcon className="w-5 h-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-emerald-400">Founders Discount Active</h3>
            <FoundersBadge size="small" showTooltip={false} />
          </div>
          <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
            You're saving <strong className="text-emerald-400">50%</strong> on your {status.upgradedTo?.toUpperCase()} plan! 
            {discountRemaining > 0 && (
              <> Discount valid for <strong className="text-emerald-400">{discountRemaining}</strong> more days.</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Status display for expired beta users
 */
function BetaExpiredStatus() {
  return (
    <div className="rounded-xl bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-surface-200 dark:bg-surface-700 text-surface-500">
          <ClockIcon className="w-5 h-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-surface-600 dark:text-surface-300">Trial Expired</h3>
            <FoundersBadge size="small" showTooltip={false} />
          </div>
          <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
            Your beta trial has ended and you've been downgraded to the Starter plan. 
            You still have your Founders Badge, but the special founders pricing has expired.
          </p>
          <p className="text-xs text-surface-500 mt-2">
            Upgrade anytime at regular prices to unlock premium features.
          </p>
          <Link
            to="/admin/billing"
            className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium text-sm transition-colors"
          >
            <ArrowUpIcon className="w-4 h-4" />
            View Plans
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact version for sidebar or header
 */
export function BetaTrialStatusCompact({ companyId }) {
  const { data: status } = useQuery({
    queryKey: ['betaTrialStatus', companyId],
    queryFn: () => fetchBetaTrialStatus(companyId),
    enabled: !!companyId,
    staleTime: 60000
  });

  if (!status?.isBetaUser) {
    return null;
  }

  const isTrialActive = status.status === 'trial_active';
  const isGracePeriod = status.status === 'grace_period';
  const isUpgraded = status.status === 'upgraded';
  
  if (isUpgraded) {
    return null; // Don't show compact status for upgraded users
  }
  
  const daysLeft = isTrialActive ? status.daysRemaining : status.graceDaysRemaining;

  return (
    <Link
      to="/admin/billing"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        isGracePeriod
          ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
          : daysLeft <= 3
            ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
            : 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20'
      }`}
    >
      <ClockIcon className="w-4 h-4" />
      <span>
        {isGracePeriod ? `${daysLeft}d grace` : `${daysLeft}d trial`}
      </span>
    </Link>
  );
}
